import { Itinerary, Day, AccomodationReservation, CarReservation, ItineraryImage, TransportationReservation } from './itineray.model';
import { Injectable } from '@angular/core';

export interface ItineraryCheckupError {
  message: string;
  routerLink: any;
}

export interface ItineraryCheckupErrors {
  info: ItineraryCheckupError[], flights: ItineraryCheckupError[], trains: ItineraryCheckupError[], ferries: ItineraryCheckupError[], buses: ItineraryCheckupError[], cars: ItineraryCheckupError[], accomodations: ItineraryCheckupError[], days: ItineraryCheckupError[], overflows?: ItineraryCheckupError[]
}

export const itineraryCheckupImagesMinResoutionRules = {
  cover: {
    width: 960, height: 540
  },
  car: {
    width: 650, height: 190
  },
  accomodation: {
    width: 460, height: 250
  },
  activity: {
    width: 380, height: 200
  },
  poi: {
    width: 480, height: 480
  }
};

@Injectable({
  providedIn: 'root'
})
export class ItineraryCheckerService {

  constructor() { }

  async check(itinerary: Itinerary) {

    const errors: ItineraryCheckupErrors = { info: [], flights: [], trains: [], ferries: [], buses: [], cars: [], accomodations: [], days: [] };

    await this.checkInfo(itinerary, errors.info);

    this.checkTransportationReservations(itinerary.id, 'flights', 'Volo', itinerary.reservations.flights, errors.flights);
    this.checkTransportationReservations(itinerary.id, 'trains', 'Treno', itinerary.reservations.trains, errors.trains);
    this.checkTransportationReservations(itinerary.id, 'ferries', 'Nave', itinerary.reservations.ferries, errors.ferries);
    this.checkTransportationReservations(itinerary.id, 'buses', 'Autobus', itinerary.reservations.buses, errors.buses);

    await this.checkCarReservations(itinerary.id, itinerary.reservations.cars, errors.cars);

    await this.checkAccomodationsReservations(itinerary.id, itinerary.reservations.accomodations, errors.accomodations);

    await this.checkDays(itinerary.id, itinerary.days, errors.days);

    return errors;
  }

  private async checkImageFieldResolution(value: ItineraryImage, fieldName: string, minResolution: { width: number, height: number }, errors: ItineraryCheckupError[], routerLink, errorPrefix: string = null) {
    if (! await this.isImageResoulutionAtLeast(value, minResolution.width, minResolution.height)) { errors.push({ message: (errorPrefix || "") + "Il campo " + fieldName + " deve avere una risoluzione di almeno " + minResolution.width + "px × " + minResolution.height + "px", routerLink }); }
  }

  private async checkInfo(itinerary: Itinerary, errors: ItineraryCheckupError[]) {

    const routerLink = ['/itineraries', itinerary.id, 'info'];

    if (!itinerary.destination) { errors.push({ message: "Il campo DESTINAZIONE è obbligatorio.", routerLink }); }

    if (!itinerary.npax) { errors.push({ message: "Il campo NUMERO PERSONE è obbligatorio.", routerLink }); }
    if (!itinerary.start) { errors.push({ message: "Il campo DATA INIZIO (DAL) è obbligatorio.", routerLink }); }
    if (!itinerary.end) { errors.push({ message: "Il campo DATA FINE (AL) è obbligatorio.", routerLink }); }

    if (!itinerary.title) { errors.push({ message: "Il campo TITOLO è obbligatorio.", routerLink }); }

    if (!itinerary.description) { errors.push({ message: "Il campo DESCRIZIONE è obbligatorio.", routerLink }); }

    if (!itinerary.coverImage || !itinerary.coverImage.ref) { errors.push({ message: "Il campo FOTO COPERTINA 1 è obbligatorio.", routerLink }); }
    if (!itinerary.secondaryImage || !itinerary.secondaryImage.ref) { errors.push({ message: "Il campo FOTO COPERTINA 2 è obbligatorio.", routerLink }); }

    await this.checkImageFieldResolution(itinerary.coverImage, "FOTO COPERTINA 1", itineraryCheckupImagesMinResoutionRules.cover, errors, routerLink);
    await this.checkImageFieldResolution(itinerary.secondaryImage, "FOTO COPERTINA 2", itineraryCheckupImagesMinResoutionRules.cover, errors, routerLink);
  }

  private checkTransportationReservations(itineraryId: string, transportationReservationsRouteSegment: string, transportationReservationName: string, transportationReservations: TransportationReservation[], errors: ItineraryCheckupError[]) {

    if (transportationReservations) {
      for (const [i, transportationReservation] of transportationReservations.entries()) {

        const routerLink = ['/itineraries', itineraryId, 'reservations', transportationReservationsRouteSegment];

        const errorPrefix = transportationReservationName + " " + (i + 1) + ": ";
        const addError = (e) => {
          errors.push({ message: errorPrefix + e, routerLink });
        };

        if (!transportationReservation.price) { addError("Il campo PREZZO è obbligatorio."); }
        if (!transportationReservation.link) { addError("Il campo LINK è obbligatorio."); }
        if (transportationReservation.routes) {
          for (const route of transportationReservation.routes) {
            if (!route.company) { addError("Il campo COMPAGNIA è obbligatorio."); }
            if (!route.code) {
              addError("Il campo CODICE è obbligatorio.");
            }
            if (!route.duration || !(route.duration.hours >= 0)) { addError("Il campo DURATA (ORE) è obbligatorio."); }
            if (!route.duration || !(route.duration.minutes >= 0)) { addError("Il campo DURATA (MINUTI) è obbligatorio."); }

            if (!route.start) { addError("Il campo DATA E ORA PARTENZA è obbligatorio."); }
            if (!route.from) { addError("Il campo DA è obbligatorio."); }
            if (!route.fromDetail) { addError("Il campo DA (DETTAGLIO) è obbligatorio."); }
            if (!route.fromPlace || !route.fromPlace.position) { addError("Il campo DA: POSIZIONE SU MAPPA è obbligatorio."); }

            if (!route.end) { addError("Il campo A è obbligatorio."); }
            if (!route.to) { addError("Il campo DATA E ORA ARRIVO è obbligatorio."); }
            if (!route.toDetail) { addError("Il campo A (DETTAGLIO) è obbligatorio."); }
            if (!route.toPlace || !route.toPlace.position) { addError("Il campo A: POSIZIONE SU MAPPA è obbligatorio."); }
          }
        }
      }
    }
  }

  private async checkCarReservations(itineraryId: string, carReservations: CarReservation[], errors: ItineraryCheckupError[]) {
    if (carReservations) {
      for (const [i, carReservation] of carReservations.entries()) {

        const routerLink = ['/itineraries', itineraryId, 'reservations', 'cars'];

        const errorPrefix = "Mezzo " + (i + 1) + ": "
        const addError = (e) => {
          errors.push({ message: errorPrefix + e, routerLink });
        };

        if (!carReservation.price) { addError("Il campo PREZZO è obbligatorio."); }
        if (!carReservation.link) { addError("Il campo LINK è obbligatorio."); }
        if (!carReservation.image || !carReservation.image.ref) { addError("Il campo FOTO è obbligatorio."); }

        await this.checkImageFieldResolution(carReservation.image, "FOTO", itineraryCheckupImagesMinResoutionRules.car, errors, routerLink, errorPrefix);

        if (!carReservation.description) { addError("Il campo DESCRIZIONE è obbligatorio."); }

        if (!carReservation.company) { addError("Il campo COMPAGNIA è obbligatorio."); }
        if (!carReservation.title) { addError("Il campo MARCA E MODELLO è obbligatorio."); }
        if (!carReservation.type) { addError("Il campo TIPO MEZZO è obbligatorio."); }

        if (!carReservation.from) { addError("Il campo DA è obbligatorio."); }
        if (!carReservation.start) { addError("Il campo DATA E ORA RITIRO è obbligatorio."); }
        if (!carReservation.fromPlace || !carReservation.fromPlace.position) { addError("Il campo DA: POSIZIONE SU MAPPA è obbligatorio."); }

        if (!carReservation.end) { addError("Il campo A è obbligatorio."); }
        if (!carReservation.to) { addError("Il campo DATA E ORA RICONSEGNA è obbligatorio."); }
        if (!carReservation.toPlace || !carReservation.toPlace.position) { addError("Il campo A: POSIZIONE SU MAPPA è obbligatorio."); }
      }
    }
  }

  private async checkAccomodationsReservations(itineraryId: string, accomodationReservations: AccomodationReservation[], errors: ItineraryCheckupError[]) {
    if (accomodationReservations) {
      for (const [i, accomodationReservation] of accomodationReservations.entries()) {

        const addError = (e) => {
          const routerLink = ['/itineraries', itineraryId, 'reservations', 'accomodations'];
          errors.push({ message: "Alloggio " + (i + 1) + ": " + e, routerLink });
        };

        if (!accomodationReservation.city) { addError("Il campo CITTA' è obbligatorio."); }
        if (!accomodationReservation.start) { addError("Il campo DATA CHECKIN' è obbligatorio."); }
        if (!accomodationReservation.end) { addError("Il campo DATA CHECKOUT' è obbligatorio."); }
        for (const [j, proposal] of accomodationReservation.proposals.entries()) {

          const routerLinkP = ['/itineraries', itineraryId, 'reservations', 'accomodations'];
          const errorPrefixP = "Alloggio " + (i + 1) + ", Proposta " + (j + 1) + ": ";
          const addErrorP = (e) => {
            errors.push({ message: errorPrefixP + e, routerLink: routerLinkP });
          };

          if (!proposal.name) { addErrorP("Il campo NOME è obbligatorio."); }

          if (!proposal.type) { addErrorP("Il campo TIPO è obbligatorio."); }
          if (!proposal.place || !proposal.place.position) { addErrorP("Il campo POSIZIONE SU MAPPA è obbligatorio."); }
          if (!proposal.price) { addErrorP("Il campo PREZZO è obbligatorio."); }
          if (!proposal.link) { addErrorP("Il campo LINK è obbligatorio."); }
          if (!proposal.image || !proposal.image.ref) { addErrorP("Il campo FOTO è obbligatorio."); }

          await this.checkImageFieldResolution(proposal.image, "FOTO", itineraryCheckupImagesMinResoutionRules.accomodation, errors, routerLinkP, errorPrefixP);

          if (!proposal.description) { addErrorP("Il campo DESCRIZIONE è obbligatorio."); }
        }
      }
    }
  }

  private async checkDays(itineraryId: string, days: Day[], errors: ItineraryCheckupError[]) {
    if (!days || days.length < 1) { errors.push({ message: "L'itinerario deve avere almeno un giorno.", routerLink: ['/itineraries', itineraryId, 'info'] }); }
    for (const [i, day] of days.entries()) {


      const routerLink = ['/itineraries', itineraryId, 'day', i + 1];
      const errorPrefix = "Giorno " + (i + 1) + ": ";
      const addError = (e, rl?) => {
        errors.push({ message: errorPrefix + e, routerLink: rl || routerLink });
      };

      if (!day.title) { addError("Il campo TITOLO è obbligatorio."); }

      if (!day.description) { addError("Il campo DESCRIZIONE è obbligatorio."); }

      // if (!day.activities || day.activities.length < 1) {
      //  addError("Il giorno deve avere avere almeno una attività.", ['/itineraries', itineraryId, 'day', i + 1, 'activities']);
      // }

      for (const [j, activity] of day.activities.entries()) {

        const routerLinkA = ['/itineraries', itineraryId, 'day', i + 1, 'activities'];
        const errorPrefixA = "Giorno " + (i + 1) + ", Attività " + (j + 1) + ": ";
        const addErrorA = (e) => {
          errors.push({ message: errorPrefixA + e, routerLink: routerLinkA });
        };

        if (!activity.reservationId) {
          if (!activity.image || !activity.image.ref) { addErrorA("Il campo FOTO è obbligatorio."); }

          await this.checkImageFieldResolution(activity.image, "FOTO", itineraryCheckupImagesMinResoutionRules.activity, errors, routerLinkA, errorPrefixA);

          if (!activity.place || !activity.place.position) { addErrorA("Il campo POSIZIONE SU MAPPA è obbligatorio."); }
        }
        if (!activity.time) { addErrorA("Il campo ORARIO è obbligatorio."); }
        if (!activity.type) { addErrorA("Il campo TIPO è obbligatorio."); }

        if (!activity.title) { addErrorA("Il campo TITOLO è obbligatorio."); }
      }

      if (day.pois) {
        for (const [j, poi] of day.pois.filter((p) => !p.generated).entries()) {

          const routerLinkP = ['/itineraries', itineraryId, 'day', i + 1, 'pois'];
          const errorPrefixP = "Giorno " + (i + 1) + ", Luogo da Scoprire " + (j + 1) + ": ";

          const addErrorP = (e) => {
            errors.push({ message: errorPrefixP + e, routerLink: routerLinkP });
          };

          if (!poi.title) { addErrorP("Il campo TITOLO è obbligatorio."); }

          if (!poi.description) { addErrorP("Il campo DESCRIZIONE è obbligatorio."); }

          if (!poi.image || !poi.image.ref) { addErrorP("Il campo FOTO è obbligatorio."); }

          await this.checkImageFieldResolution(poi.image, "FOTO", itineraryCheckupImagesMinResoutionRules.poi, errors, routerLinkP, errorPrefixP);

          if (!poi.place || !poi.place.position) { addErrorP("Il campo POSIZIONE SU MAPPA è obbligatorio."); }
        }
      }

    }

  }

  private isImageResoulutionAtLeast(image: ItineraryImage, minWidth: number, minHeight: number) {
    return new Promise((resolve, reject) => {
      if (image && image.url) {
        const img = new Image();
        img.addEventListener("load", () => {
          if (img.naturalHeight >= minHeight && img.naturalWidth >= minWidth) {
            resolve(true);
          } else {
            resolve(false);
          }
        });
        img.addEventListener("error", () => { resolve(false); });
        img.src = image.url;
      } else {
        resolve(false);
      }
    });
  }

}
